'use client'

import * as React from 'react'
import { useRef, useState } from 'react'
import { useForm, useStore } from '@tanstack/react-form'
import { useMutation, useQuery } from '@tanstack/react-query'
import { CalendarIcon, SearchIcon, UserPlusIcon } from 'lucide-react'
import { z } from 'zod'

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldError,
  FieldLabel,
  Input,
  Separator,
  Skeleton,
  toast,
} from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import { getInitials } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import type { AssignModuleDialogBaseProps, AssignModuleDialogProps, AssignModuleSubmitData, ChildItem } from './types'
import { useAuthClient, useSharedTRPC } from '../../context'
import { SchoolClassSelectBase } from '../school-class-select'
import { ScheduleAssignDialog } from './schedule-assign-dialog'

const assignSchema = z.object({
  title: z.string().min(1, 'Nama tugas wajib diisi'),
  childIds: z.array(z.string()).min(1, 'Pilih minimal 1 peserta'),
})

function getSubtitle(child: ChildItem, type: 'school' | 'web'): string {
  if (type === 'school') {
    return child.premiumId ? 'Akses premium' : 'Akses terbatas'
  }
  const seat = child.premiumId ? 'Premium' : 'Terbatas'
  return child.schoolClass?.name ? `${child.schoolClass.name} • ${seat}` : seat
}

interface ChildRowProps {
  child: ChildItem
  selected: boolean
  onToggle: (id: string) => void
  type: 'school' | 'web'
}

function ChildRow({ child, selected, onToggle, type }: ChildRowProps) {
  const subtitle = getSubtitle(child, type)
  return (
    <div
      role='button'
      tabIndex={0}
      onClick={() => onToggle(child.id)}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onToggle(child.id)}
      className='srd:flex srd:items-center srd:gap-2 srd:px-4 srd:py-3 srd:cursor-pointer srd:hover:bg-grey-10 srd:transition-colors srd:select-none'
    >
      <Avatar profile='user' className='srd:size-8 srd:shrink-0'>
        <AvatarImage src={child.image ?? undefined} alt={child.name} />
        <AvatarFallback>{getInitials(child.name)}</AvatarFallback>
      </Avatar>
      <div className='srd:flex-1 srd:min-w-0'>
        <p className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:truncate')}>{child.name}</p>
        <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:truncate')}>{subtitle}</p>
      </div>
      <div role='presentation' onClick={(e) => e.stopPropagation()}>
        <Checkbox checked={selected} onCheckedChange={() => onToggle(child.id)} aria-label={`Pilih ${child.name}`} />
      </div>
    </div>
  )
}

interface EmptyStateProps {
  type: 'empty-org' | 'filtered-empty' | 'no-class-member'
  childLabel: string
  onAddChild?: () => void
}

function EmptyState({ type, childLabel, onAddChild }: EmptyStateProps) {
  if (type === 'no-class-member') {
    return (
      <div className='srd:flex srd:flex-col srd:items-center srd:gap-2 srd:py-10 srd:text-center'>
        <p className={typographyVariants({ variant: 'label-lg' })}>Tidak ada {childLabel} tersedia</p>
        <p className={cn(typographyVariants({ variant: 'body-md' }))}>
          Kamu belum ditugaskan mengampu di kelas mana pun di sekolah ini.
        </p>
      </div>
    )
  }

  if (type === 'empty-org') {
    return (
      <div className='srd:flex srd:flex-col srd:items-center srd:gap-2 srd:py-10 srd:text-center'>
        <p className={typographyVariants({ variant: 'label-lg' })}>Belum ada {childLabel}</p>

        <p className={cn(typographyVariants({ variant: 'body-md' }))}>
          Tambahkan {childLabel} terlebih dahulu sebelum membuat tugas.
        </p>

        <Button variant='outline' className='srd:rounded-full srd:gap-2' onClick={onAddChild} disabled={!onAddChild}>
          <UserPlusIcon className='srd:size-4' />
          Tambah {childLabel}
        </Button>
      </div>
    )
  }

  return (
    <div className='srd:flex srd:flex-col srd:items-center srd:gap-2 srd:py-10 srd:text-center'>
      <p className={typographyVariants({ variant: 'label-lg' })}>Tidak ada hasil</p>
      <p className={cn(typographyVariants({ variant: 'body-md' }))}>Coba ubah filter atau kata kunci pencarian.</p>
    </div>
  )
}

export function AssignModuleDialogBase({
  type,
  open,
  onOpenChange,
  module,
  children,
  isLoading,
  teams,
  isLoadingTeams,
  showDash,
  search,
  onSearchChange,
  teamId,
  onTeamChange,
  onAddChild,
  onSubmit,
  isSubmitting,
}: AssignModuleDialogBaseProps) {
  const childLabel = type === 'school' ? 'murid' : 'anak'
  const titleLabel = type === 'school' ? 'Pilih murid untuk tugas belajar' : 'Pilih anak untuk tugas belajar'

  const [step, setStep] = useState<'assign' | 'schedule'>('assign')
  const [pendingFormData, setPendingFormData] = useState<{ title: string; childIds: string[] } | null>(null)

  const submitActionRef = useRef<'now' | 'schedule'>('now')

  const form = useForm({
    defaultValues: {
      title: module.title,
      childIds: [] as string[],
    },
    validators: { onSubmit: assignSchema },
    onSubmit: async ({ value }) => {
      if (submitActionRef.current === 'schedule') {
        setPendingFormData({ title: value.title, childIds: value.childIds })
        setStep('schedule')
      } else {
        await onSubmit({ title: value.title, childIds: value.childIds })
      }
    },
  })

  const childIds = useStore(form.store, (s) => s.values.childIds)
  const canSubmit = useStore(form.store, (s) => s.canSubmit)

  const hasFilters = !!search || !!teamId
  const isEmptyOrg = children.length === 0 && !hasFilters && !isLoading
  const isFilteredEmpty = children.length === 0 && hasFilters && !isLoading
  const isNoClassMember = isEmptyOrg && !!showDash && (teams?.length ?? 0) === 0

  const allSelected = children.length > 0 && childIds.length === children.length
  const someSelected = childIds.length > 0 && childIds.length < children.length

  function toggleChild(id: string) {
    const current = form.getFieldValue('childIds')
    const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id]
    form.setFieldValue('childIds', next)
  }

  function toggleAll() {
    if (allSelected) {
      form.setFieldValue('childIds', [])
    } else {
      form.setFieldValue(
        'childIds',
        children.map((c) => c.id),
      )
    }
  }

  function handleClose(v: boolean) {
    if (!v) {
      setStep('assign')
      setPendingFormData(null)
    }
    onOpenChange(v)
  }

  const selectedCount = childIds.length

  return (
    <>
      <Dialog open={open && step === 'assign'} onOpenChange={handleClose}>
        <DialogContent className='srd:max-w-lg srd:w-full' showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>{titleLabel}</DialogTitle>
            <DialogDescription>
              Tentukan nama tugas, peserta, dan jadwal untuk menjadikan modul ini menjadi tugas yang siap dikerjakan.
            </DialogDescription>
          </DialogHeader>

          <div className='srd:flex srd:justify-center'>
            <img
              src={module.coverUrl}
              alt={module.title}
              className='srd:w-full srd:max-w-[170px] srd:aspect-4/3 srd:rounded-xl srd:object-cover'
              draggable={false}
            />
          </div>

          <form.Field name='title'>
            {(field) => {
              const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid
              return (
                <Field data-invalid={isInvalid || undefined}>
                  <FieldLabel>Nama tugas</FieldLabel>
                  <Input
                    size='sm'
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    aria-invalid={isInvalid}
                    placeholder={module.title}
                  />
                  {!isInvalid && (
                    <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-grey-60')}>
                      Contoh: Tugas IPAS Kelas 1A
                    </p>
                  )}
                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              )
            }}
          </form.Field>

          <div className={cn('srd:flex srd:gap-2', type === 'school' ? 'srd:flex-row' : 'srd:flex-col')}>
            {type === 'school' && (
              <SchoolClassSelectBase
                size='sm'
                value={teamId ?? ''}
                onValueChange={(v) => onTeamChange?.(v || undefined)}
                teams={teams ?? []}
                isLoading={isLoadingTeams}
                showDash={showDash}
                showNoClass={!showDash}
                allowAll={!showDash}
                className='srd:flex-1'
              />
            )}
            <div className='srd:relative srd:flex-1'>
              <SearchIcon className='srd:absolute srd:left-3 srd:top-1/2 srd:-translate-y-1/2 srd:size-4 srd:text-grey-60 srd:pointer-events-none' />
              <Input
                size='sm'
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder='Cari'
                className='srd:pl-9'
              />
            </div>
          </div>

          <form.Field name='childIds'>
            {(field) => {
              const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid
              return (
                <Field data-invalid={isInvalid || undefined}>
                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              )
            }}
          </form.Field>

          <div>
            <div className='srd:flex srd:items-center srd:justify-between srd:px-4 srd:py-2'>
              <p className={cn(typographyVariants({ variant: 'label-md' }), 'srd:text-blue-100')}>
                {selectedCount} terpilih
              </p>
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected}
                onCheckedChange={toggleAll}
                aria-label='Pilih semua'
                disabled={children.length === 0}
              />
            </div>

            <Separator />

            <div className='srd:max-h-[230px] srd:overflow-y-auto'>
              {isLoading && (
                <div className='srd:flex srd:flex-col srd:gap-1 srd:p-4'>
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className='srd:h-14 srd:w-full srd:rounded-lg' />
                  ))}
                </div>
              )}

              {!isLoading && isNoClassMember && <EmptyState type='no-class-member' childLabel={childLabel} />}

              {!isLoading && isEmptyOrg && !isNoClassMember && (
                <EmptyState type='empty-org' childLabel={childLabel} onAddChild={onAddChild} />
              )}

              {!isLoading && isFilteredEmpty && <EmptyState type='filtered-empty' childLabel={childLabel} />}

              {!isLoading &&
                children.map((child) => (
                  <ChildRow
                    key={child.id}
                    child={child}
                    selected={childIds.includes(child.id)}
                    onToggle={toggleChild}
                    type={type}
                  />
                ))}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant='outline'
              disabled={!canSubmit || isSubmitting || selectedCount === 0}
              onClick={() => {
                submitActionRef.current = 'schedule'
                void form.handleSubmit()
              }}
            >
              <CalendarIcon className='srd:size-4 srd:mr-2' />
              Jadwalkan
            </Button>
            <Button
              disabled={!canSubmit || isSubmitting || selectedCount === 0}
              loading={isSubmitting && submitActionRef.current === 'now'}
              onClick={() => {
                submitActionRef.current = 'now'
                void form.handleSubmit()
              }}
            >
              Tugaskan sekarang
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {pendingFormData && (
        <ScheduleAssignDialog
          open={open && step === 'schedule'}
          onOpenChange={(v) => {
            if (!v) handleClose(false)
          }}
          type={type}
          onSchedule={async ({ startDate, endDate }) => {
            await onSubmit({
              title: pendingFormData.title,
              childIds: pendingFormData.childIds,
              startDate,
              endDate,
            })
          }}
          onBack={() => setStep('assign')}
          isSubmitting={isSubmitting}
        />
      )}
    </>
  )
}

export function AssignModuleDialog({
  type,
  open,
  onOpenChange,
  module,
  onSuccess,
  onAddChild,
}: AssignModuleDialogProps) {
  const trpc = useSharedTRPC()
  const authClient = useAuthClient()
  const { data: memberRole, isPending: isRolePending } = authClient.useActiveMemberRole()

  const isAdminOrOwner = memberRole?.role === 'owner' || memberRole?.role === 'admin'

  const [search, setSearch] = useState('')
  const [teamId, setTeamId] = useState<string | undefined>(undefined)

  const myTeamsOnly = type === 'school' && !isAdminOrOwner && !teamId && !isRolePending

  const { data: childrenData, isLoading: isLoadingChildren } = useQuery(
    trpc.myOrganization.listChildren.queryOptions({
      pageSize: 'all',
      search: search || undefined,
      teamId,
      myTeamsOnly: myTeamsOnly || undefined,
      includeSchoolClass: true,
    }),
  )

  const allTeamsQuery = useQuery({
    ...trpc.myOrganization.getAllTeams.queryOptions(),
    enabled: type === 'school' && !isRolePending && isAdminOrOwner,
  })

  const myTeamsQuery = useQuery({
    ...trpc.myOrganization.getMyTeams.queryOptions(),
    enabled: type === 'school' && !isRolePending && !isAdminOrOwner,
  })

  const isLoadingTeams =
    type === 'school' && (isRolePending || (isAdminOrOwner ? allTeamsQuery.isLoading : myTeamsQuery.isLoading))

  const createCourseMutation = useMutation(
    trpc.lesson.createCourse.mutationOptions({
      onSuccess: (data) => {
        toast.success('Tugas berhasil dibuat')
        onOpenChange(false)
        onSuccess?.({ courseId: data.id })
      },
      onError: () => {
        toast.error('Gagal membuat tugas, coba lagi')
      },
    }),
  )

  const children =
    childrenData?.data.map((c) => ({
      id: c.id,
      name: c.name ?? '',
      image: c.image,
      premiumId: c.premiumId,
      schoolClass: c.schoolClass,
    })) ?? []

  const rawTeamsData = isAdminOrOwner ? allTeamsQuery.data : myTeamsQuery.data
  const teams =
    rawTeamsData?.map((t) => ({
      id: t.id,
      name: t.name,
      image: (t as { image?: string | null }).image ?? null,
    })) ?? []

  async function handleSubmit(data: AssignModuleSubmitData) {
    await createCourseMutation.mutateAsync({
      ...(module.id ? { lessonModuleId: module.id } : { lessonModuleSlug: module.slug ?? '' }),
      title: data.title,
      childIds: data.childIds,
      startDate: data.startDate,
      endDate: data.endDate ?? undefined,
    })
  }

  return (
    <AssignModuleDialogBase
      type={type}
      open={open}
      onOpenChange={onOpenChange}
      module={module}
      children={children}
      isLoading={isLoadingChildren}
      teams={teams}
      isLoadingTeams={isLoadingTeams}
      showDash={!isAdminOrOwner}
      search={search}
      onSearchChange={setSearch}
      teamId={teamId}
      onTeamChange={setTeamId}
      onAddChild={onAddChild}
      onSubmit={handleSubmit}
      isSubmitting={createCourseMutation.isPending}
    />
  )
}
